import { useMemo } from "react"
import { Link } from "react-router-dom"
import { useTranslation } from "@/i18n"
import { useBookmarks } from "@/hooks/useSocial"
import { useEventsByIds } from "@/hooks/useProfiles"
import { useAuth } from "@/state/AuthContext"
import { NoteCard } from "@/components/NoteCard"
import { EmptyState, Skeleton } from "@/components/Feedback"
import { Icon } from "@/components/Icon"
import type { NostrEvent } from "@/nostr/types"

/** Bookmarked notes (kind 10003), resolved against the connected relays. */
export function BookmarksPage() {
  const { t } = useTranslation()
  const { pubkey, canSign } = useAuth()
  const bookmarks = useBookmarks()
  const events = useEventsByIds(bookmarks.noteIds)

  const notes = useMemo(
    () =>
      bookmarks.noteIds
        .map((id) => events.get(id))
        .filter((event): event is NostrEvent => Boolean(event)),
    [bookmarks.noteIds, events],
  )
  const missing = bookmarks.noteIds.length - notes.length

  if (!pubkey) {
    return (
      <div className="page">
        <EmptyState
          icon="bookmark"
          title={t("Log in to use bookmarks")}
          description={t("Bookmarks are stored in your own kind 10003 event, not on a server.")}
          action={
            <Link to="/settings" className="btn btn-primary">
              {t("Settings")}
            </Link>
          }
        />
      </div>
    )
  }

  return (
    <div className="page bookmarks-page">
      <header className="page-header">
        <div className="page-header-main">
          <h1 className="page-title">{t("Bookmarks")}</h1>
          <p className="page-subtitle">
            {bookmarks.noteIds.length} saved · {canSign ? t("signed in") : t("read-only")}
          </p>
        </div>
      </header>

      {bookmarks.loading && notes.length === 0 && <Skeleton lines={3} />}

      {!bookmarks.loading && bookmarks.noteIds.length === 0 && (
        <EmptyState
          icon="bookmark"
          title={t("No bookmarks yet")}
          description={t("Open a note and press the bookmark icon to save it here.")}
          action={
            <Link to="/" className="btn btn-primary">
              <Icon name="home" size={16} /> {t("Back home")}
            </Link>
          }
        />
      )}

      {notes.map((note) => (
        <NoteCard key={note.id} event={note} showBookmark />
      ))}

      {missing > 0 && (
        <p className="muted small">
          {missing} bookmark{missing === 1 ? "" : "s"} could not be loaded from the connected relays.
        </p>
      )}
    </div>
  )
}
